import { Container, Typography } from '@cleartrip/ct-design-components'
import React, { memo } from 'react'

import AirlineLogos from './AirlineLogos'
import RevampTooltip from './RevampTooltip'
import { TUPLE_TOOLTIP } from '../config/tupleTheme'
import type { TupleAirline } from '../model/types'

export interface AirlineBlockProps {
  airlines: TupleAirline[]
  density?: 'default' | 'compact'
  flightNumbersLabel?: string
}

const airlineTitle = (airlines: TupleAirline[]): string =>
  airlines
    .map(a => a.name)
    .filter(Boolean)
    .join(', ')

const uniqueAirlineCount = (airlines: TupleAirline[]): number => {
  const seen = new Set<string>()
  for (const airline of airlines) {
    if (airline.code) seen.add(airline.code)
  }
  return seen.size
}

const AIRLINE_NAME_TOOLTIP_GAP = TUPLE_TOOLTIP.gap - 4

const CSS_DEFAULT = { width: 160, minWidth: 160, maxWidth: 160, paddingRight: 12 }
const CSS_INNER = { minWidth: 0 }
const CSS_GAP8 = { columnGap: 8 }

const AirlineBlock: React.FC<AirlineBlockProps> = ({ airlines, density = 'default', flightNumbersLabel }) => {
  const lead = airlines[0]
  if (!lead) return null

  const airlineCount = uniqueAirlineCount(airlines)
  const isCompact = density === 'compact'
  const logoSize = airlineCount >= 2 ? (isCompact ? 15 : 24) : isCompact ? 20 : 32
  const showFlightNumber = density === 'default'
  const fullTitle = airlineTitle(airlines)
  const nameVariant = isCompact ? 'B2' : 'HM4'
  const displayFlightNumbers = flightNumbersLabel || lead.flightNumber.replace('-', ' ')

  const airlineTooltipContent = (
    <Typography className='ws-nowrap' color='neutral' componentNode='p' variant='B3'>
      {fullTitle}
    </Typography>
  )

  return (
    <Container
      className='flex flex-no-shrink o-hidden'
      css={density === 'default' ? { ...CSS_DEFAULT, ...CSS_GAP8 } : CSS_GAP8}
    >
      <RevampTooltip align='center' content={airlineTooltipContent} position='top'>
        <Container className='c-pointer flex-no-shrink'>
          <AirlineLogos airlines={airlines} size={logoSize} />
        </Container>
      </RevampTooltip>

      <Container className='flex flex-column flex-1 o-hidden' css={CSS_INNER}>
        <RevampTooltip
          align='center'
                    content={airlineTooltipContent}
          gap={AIRLINE_NAME_TOOLTIP_GAP}
          position='top'
          style={{ width: 'fit-content', maxWidth: '100%' }}
        >
          <Typography className='tt-ellipsis ws-nowrap c-pointer' color='heading' variant={nameVariant}>
            {fullTitle}
          </Typography>
        </RevampTooltip>

        {showFlightNumber && displayFlightNumbers && (
          <RevampTooltip
            align='center'
            content={
              <Typography className='ws-nowrap' color='neutral' componentNode='p' variant='B3'>
                {displayFlightNumbers}
              </Typography>
            }
            position='bottom'
            style={{ width: 'fit-content', maxWidth: '100%' }}
          >
            <Typography className='tt-ellipsis ws-nowrap c-pointer' color='subheading' componentNode='p' variant='B4'>
              {displayFlightNumbers}
            </Typography>
          </RevampTooltip>
        )}
      </Container>
    </Container>
  )
}

export default memo(AirlineBlock)
